import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import Stats from './components/Stats';
import ReportStats from './components/ReportStats';
import Calculator from './components/Calculator';
import HowItWorks from './components/HowItWorks';
import Industries from './components/Industries';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import ContactForm from './components/ContactForm';
import IndustryPage from './pages/IndustryPage';
import AboutPage from './pages/AboutPage';
import IntegrationsPage from './pages/IntegrationsPage';
import AiReceptionistPage from './pages/AiReceptionistPage';
import BestAiReceptionistPage from './pages/BestAiReceptionistPage';
import VirtualReceptionistPage from './pages/VirtualReceptionistPage';
import AIWidget from './components/AIWidget';
import SafeIcon from './common/SafeIcon';
import { industryContent } from './data/industryContent';

const MainLanding = () => (
  <>
    <Hero />
    <Stats />
    <ReportStats />
    <Calculator />
    <HowItWorks />
    <Industries />
    <Pricing />
    <FAQ />
    <ContactForm />
  </>
);

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-slate-950 text-slate-50 font-sans selection:bg-blue-500/30 flex flex-col">
        <Header />
        <AIWidget />

        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<MainLanding />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/ai-receptionist-for-small-business" element={<AiReceptionistPage />} />
            <Route path="/best-ai-receptionist-for-small-business" element={<BestAiReceptionistPage />} />
            <Route path="/virtual-receptionist-for-small-business" element={<VirtualReceptionistPage />} />
            <Route path="/hvac" element={<IndustryPage content={industryContent.hvac} />} />
            <Route path="/dentists" element={<IndustryPage content={industryContent.dentists} />} />
            <Route path="/legal" element={<IndustryPage content={industryContent.legal} />} />
            <Route path="/real-estate" element={<IndustryPage content={industryContent.realestate} />} />
            <Route path="/med-spa" element={<IndustryPage content={industryContent.medspa} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        <footer className="border-t border-slate-800 bg-slate-950 px-4 py-16">
          <div className="mx-auto mb-12 grid max-w-7xl gap-12 md:grid-cols-5">
            <div className="col-span-2">
              <div className="mb-6 flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
                  <SafeIcon name="PhoneCall" className="text-xl text-white" />
                </div>
                <span className="text-2xl font-bold tracking-tight text-white">
                  Auto<span className="text-blue-500">Reception</span>
                </span>
              </div>

              <p className="mb-8 max-w-sm leading-relaxed text-slate-400">
                The 24/7 AI voice receptionist that captures every lead, qualifies every caller, and schedules every appointment.
              </p>

              <div className="space-y-1 text-sm text-slate-500">
                <p className="mb-2 font-semibold text-slate-400">AutoReception is a part of:</p>
                <p className="font-medium text-slate-300">Konsulentbixen</p>
                <p>Njalsgade 21F, 2nd floor</p>
                <p>2300 Copenhagen, Denmark</p>
                <p className="pt-1">VAT ID: DK41368071</p>
              </div>
            </div>

            <div>
              <h4 className="mb-6 text-xs font-bold uppercase tracking-wider text-white">Industries</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><Link to="/hvac" className="transition-colors hover:text-blue-400">HVAC &amp; Plumbing</Link></li>
                <li><Link to="/dentists" className="transition-colors hover:text-blue-400">Dentists</Link></li>
                <li><Link to="/legal" className="transition-colors hover:text-blue-400">Legal</Link></li>
                <li><Link to="/real-estate" className="transition-colors hover:text-blue-400">Real Estate</Link></li>
                <li><Link to="/med-spa" className="transition-colors hover:text-blue-400">Med Spas</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="mb-6 text-xs font-bold uppercase tracking-wider text-white">Content</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li>
                  <Link to="/ai-receptionist-for-small-business" className="transition-colors hover:text-blue-400">
                    AI Receptionist for Small Business
                  </Link>
                </li>
                <li>
                  <Link to="/best-ai-receptionist-for-small-business" className="transition-colors hover:text-blue-400">
                    Best AI Receptionist Comparison
                  </Link>
                </li>
                <li>
                  <Link to="/virtual-receptionist-for-small-business" className="transition-colors hover:text-blue-400">
                    Virtual Receptionist Guide
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="mb-6 text-xs font-bold uppercase tracking-wider text-white">Legal &amp; Support</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li>
                  <Link to="/integrations" className="transition-colors hover:text-blue-400">
                    Integrations
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="transition-colors hover:text-blue-400">
                    About Us
                  </Link>
                </li>
                <li>
                  <a href="https://legal.konsulentbixen.dk/#privacy" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-blue-400">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="https://legal.konsulentbixen.dk/#terms" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-blue-400">
                    General Terms of Service
                  </a>
                </li>
                <li>
                  <a href="https://legal.konsulentbixen.dk/#antispam" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-blue-400">
                    Anti-spam Policy
                  </a>
                </li>
                <li>
                  <button onClick={() => window.showConsentPreferences?.()} className="text-left transition-colors hover:text-blue-400">
                    Manage Cookie Settings
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 border-t border-slate-900 pt-8 text-xs text-slate-500 md:flex-row">
            <p>© {new Date().getFullYear()} AutoReception by Konsulentbixen. All rights reserved.</p>

            <div className="flex items-center gap-6">
              <span className="flex items-center gap-1">
                <SafeIcon name="Shield" className="text-[10px]" />
                Secure Data Processing
              </span>
              <span className="flex items-center gap-1">
                <SafeIcon name="Globe" className="text-[10px]" />
                GDPR Compliant
              </span>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;